import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Dialog } from '@headlessui/react';
import dayjs from 'dayjs';
import { cls } from '@/utils';

// 아이콘들
const Badge = ({ checked, txt }) => {
  if (checked) {
    return (
      <div className="relative w-[88px] h-[88px] flex justify-center items-center">
        <p
          className={cls(
            'bg-transparent z-30 absolute font-extrabold top-1/2 left-1/2 text-white text-center leading-[16px]',
            'rotate-[-15deg] translate-x-[-48%] translate-y-[-47%]',
          )}
        >
          {txt}
        </p>
        <img
          className="absolute z-10"
          src="https://cdn.shopify.com/s/files/1/0528/8266/9745/files/3d8b0a6f7c8747cf6eda6ab3499824e80bbfb517.png?v=1780511173"
        />
        <motion.img
          className="absolute w-[71%] h-[71%] z-20"
          src="https://cdn.shopify.com/s/files/1/0528/8266/9745/files/7918071df91505a17528f4c5173a04fe1f189a13.png?v=1780511173"
          initial={{ scale: 0, rotate: -45, opacity: 0, y: -20 }}
          animate={{
            scale: [0, 1.6, 0.8, 1.3, 0.95, 1.1, 1],
            rotate: [-45, 15, -10, 5, -3, 0],
            opacity: [0, 1, 1, 1, 1, 1, 1],
            y: [-20, 0, 0, 0, 0, 0, 0],
          }}
          transition={{
            duration: 0.8,
            times: [0, 0.25, 0.4, 0.55, 0.7, 0.85, 1],
            ease: [0.34, 1.8, 0.64, 1],
            delay: 0.5,
          }}
        />
      </div>
    );
  } else {
    return (
      <div className="relative w-full h-full">
        <p
          className={cls(
            'bg-transparent z-20 absolute font-extrabold top-1/2 left-1/2 text-white text-center leading-[16px]',
            'rotate-[-15deg] translate-x-[-48%] translate-y-[-47%]',
          )}
        >
          {txt}
        </p>
        <img
          className="w-[88px] h-[88px]"
          src="https://cdn.shopify.com/s/files/1/0528/8266/9745/files/3d8b0a6f7c8747cf6eda6ab3499824e80bbfb517.png?v=1780511173"
        />
      </div>
    );
  }
};

function DayTile({ item, index, locale, totalDays, days, rewardingType, checkedInDates }) {
  const now = dayjs().tz('America/Los_Angeles', true).format('YYYYMMDDHHmm');

  const isToday = Number(now) >= item.date && (index + 1 < days.length ? Number(now) < days[index + 1].date : true);
  const isDone = checkedInDates.includes(item.date); // 체크인 날짜 배열에 현재 날짜가 있는지 확인
  const displayPoints = rewardingType === 'escalating' ? item.escalatingPoint : item.points;
  const date = Number(String(item.date).slice(6, 8));

  const txt = locale === 'ko' ? `${displayPoints}P 적립` : `Earn ${displayPoints}P`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="snap-start shrink-0 pt-2"
    >
      <div className="flex flex-col items-center gap-2">
        <div className="text-center w-full">
          <p className="text-base text-[#B5BEC6] mb-1 font-medium">{item.weekday[locale]}</p>
          <p className="bg-[#DBECD87A] text-lg font-bold text-[#4A5660]">{date}</p>
        </div>

        <motion.div
          whileHover={item.status !== 'future' ? {} : { y: -5 }}
          className={`
            w-full h-full border-t-black px-1 py-3 border-t border-r-[#0000004D] border-r flex flex-col gap-y-2 items-center justify-center relative overflow-hidden transition-colors
          `}
          style={index + 1 === Number(totalDays) ? { borderRight: '0px' } : {}}
        >
          <Badge checked={isDone} txt={txt} />
          <span
            className={`text-sm md:text-lg font-medium`}
            style={isToday ? { color: '#249c68', textDecoration: 'underline' } : {}}
          >
            Day {index + 1}
          </span>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default function EventCalendar(props) {
  const { token, locale, eventName, widgetTitle, loginUrl, loginToPlay } = props;

  const [data, setData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bonusModal, setBonusModal] = useState(false);
  const scrollRef = useRef(null);

  const campaign = data?.campaign;
  const checkIn = data?.checkIn;
  const days = campaign?.days || [];
  const rewardingType = campaign?.rewardingType || 'fixed';
  const checkedInDates = checkIn?.dates || [];

  // 오늘 날짜의 date 값을 찾기
  const now = dayjs().tz('America/Los_Angeles', true).format('YYYYMMDDHHmm');
  const todayDateValue = days.find((day, index) => {
    return Number(now) >= day.date && (index + 1 < days.length ? Number(now) < days[index + 1].date : true);
  })?.date;

  // 오늘 이미 체크인했는지 확인
  const hasCheckedInToday = todayDateValue ? checkedInDates.includes(todayDateValue) : false;

  // 📍 시작 시 오늘 날짜로 자동 스크롤
  useEffect(() => {
    if (scrollRef.current && days.length > 0) {
      const now = Number(dayjs().tz('America/Los_Angeles', true).format('YYYYMMDDHHmm'));
      const todayIndex = days.findIndex((day, index) => {
        return now >= day.date && (index + 1 < days.length ? now < days[index + 1].date : true);
      });
      if (todayIndex >= 0) {
        const scrollPos = todayIndex * 96 - 100;
        scrollRef.current.scrollTo({ left: scrollPos, behavior: 'smooth' });
      }
    }
  }, [days]);

  useEffect(() => {
    fetch('https://checkinchallenge-qmlofqf3ja-uc.a.run.app', {
      method: 'GET',
      headers: {
        Authorization: token,
        'x-campaign-id': eventName,
        'x-locale': locale,
      },
    })
      .then((res) => res.json())
      .then((responseData) => {
        setData(responseData);
      })
      .catch((error) => {
        console.error('Failed to fetch campaign:', error);
      });

    // test data
    // setData({
    //   campaign: {
    //     id: 'wellness-2026-06',
    //     label: 'Wellness',
    //     start: '2024-06-08T00:00:00Z',
    //     end: '2024-06-14T23:59:59Z',
    //     rewardingType: 'fixed',
    //     completionBonus: 1000,
    //     days: [
    //       {
    //         date: 202606010700,
    //         points: 100,
    //         weekday: {
    //           ko: '월',
    //           en: 'Mon',
    //         },
    //       },
    //       {
    //         date: 202606020700,
    //         points: 100,
    //         weekday: {
    //           ko: '화',
    //           en: 'Tue',
    //         },
    //       },
    //       {
    //         date: 202606030700,
    //         points: 200,
    //         weekday: {
    //           ko: '수',
    //           en: 'Wed',
    //         },
    //       },
    //       {
    //         date: 202606040700,
    //         points: 100,
    //         weekday: {
    //           ko: '목',
    //           en: 'Thu',
    //         },
    //       },
    //       {
    //         date: 202606050700,
    //         points: 200,
    //         weekday: {
    //           ko: '금',
    //           en: 'Fri',
    //         },
    //       },
    //       {
    //         date: 202606060700,
    //         points: 100,
    //         weekday: {
    //           ko: '토',
    //           en: 'Sat',
    //         },
    //       },
    //       {
    //         date: 202606070700,
    //         points: 300,
    //         weekday: {
    //           ko: '일',
    //           en: 'Sun',
    //         },
    //       },
    //     ],
    //   },
    //   checkIn: {
    //     dates: [202606080700, 202606100700],
    //     lastCheckIn: {
    //       _seconds: 1780470000,
    //       _nanoseconds: 0,
    //     },
    //     completionBonus: false,
    //     totalCheckIns: 1,
    //   },
    // });
  }, [token, eventName, locale]);

  const handleCheckIn = async () => {
    setIsSubmitting(true);

    try {
      const res = await fetch('https://checkinchallenge-qmlofqf3ja-uc.a.run.app', {
        method: 'POST',
        headers: {
          Authorization: token,
          'x-campaign-id': eventName,
          'x-locale': locale,
        },
      });

      const result = await res.json();

      // 체크인 성공 시 dates만 업데이트
      if (result.success && result.checkIn?.dates) {
        setData((prev) => ({
          ...prev,
          checkIn: {
            ...prev.checkIn,
            dates: result.checkIn.dates,
          },
        }));

        // bonusAwarded가 있으면 모달 띄우기
        if (result.bonusAwarded) {
          setBonusModal(true);
        }
      }
    } catch (error) {
      console.error('Check-in failed:', error);
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
      }, 500);
    }
  };

  const handleLogin = () => {
    window.location.href = loginUrl;
  }

  if (!campaign || days.length === 0) {
    return (
      <div className="w-full flex items-center justify-center font-sans text-gray-800 py-20">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  const checkInBtn = locale === 'ko' ? '세븐 챌린지 체크인 하기' : 'Check In for Seven Challenge';

  return (
    <div className="w-full flex items-center justify-center font-sans text-gray-800">
      <div className="lg:col-span-8 w-full">
        <motion.div initial={{ opacity: 1, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="relative">
          <div className="">
            <div
              className="relative group bg-white rounded-lg px-1"
              style={{
                boxShadow: '2.03px 16.26px 19.31px 0px #00000017',
              }}
            >
              {/* --- 헤더 섹션 --- */}
              <div className="px-4 pt-4 lg:pt-8 text-center">
                <p
                  className="text-[#090909] text-base lg:text-3xl font-semibold"
                  dangerouslySetInnerHTML={{ __html: widgetTitle }}
                ></p>
              </div>

              {/* 스크롤 영역 */}
              <div
                ref={scrollRef}
                className="flex overflow-x-auto pb-6 pt-4 snap-x snap-mandatory scrollbar-hide px-1"
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                {days.map((item, index) => (
                  <DayTile
                    key={index}
                    item={item}
                    index={index}
                    locale={locale}
                    totalDays={days.length}
                    days={days}
                    rewardingType={rewardingType}
                    checkedInDates={checkedInDates}
                  />
                ))}
              </div>
              <div className="absolute right-0 top-0 bottom-6 w-16 bg-gradient-to-l from-white to-transparent pointer-events-none" />
            </div>

            <div className="bg-[#B6FFBA66] flex justify-start font-bold w-fit mx-auto px-3 lg:px-5 mt-5 lg:mt-8">
              <span className="text-[#395A30] text-sm lg:text-xl">
                {locale === 'ko' ? (
                  <>
                    완주시 <span className="text-[#D23B28] text-xl lg:text-4xl">1,000P</span> 추가 적립!
                  </>
                ) : (
                  <>
                    Get an extra <span className="text-[#D23B28] text-xl lg:text-4xl">1,000P</span>points when you
                    complete it!
                  </>
                )}
              </span>
            </div>

            {/* 버튼 */}
            {token ? (
              <motion.button
                whileHover={!isSubmitting && !hasCheckedInToday ? { scale: 1.01 } : {}}
                whileTap={!isSubmitting && !hasCheckedInToday ? { scale: 0.98 } : {}}
                animate={{
                  opacity: isSubmitting || hasCheckedInToday ? 0.6 : 1,
                  scale: isSubmitting ? 0.98 : 1,
                }}
                className={cls(
                  'py-4 rounded-xl text-white font-bold text-lg shadow-lg flex items-center justify-center gap-2 transition-all mt-10 relative overflow-hidden group mx-auto w-[95%]',
                  !hasCheckedInToday && 'animate-pulse',
                  isSubmitting || hasCheckedInToday ? 'cursor-not-allowed' : 'cursor-pointer',
                )}
                style={{ backgroundColor: isSubmitting || hasCheckedInToday ? '#6B9E8A' : '#249C68' }}
                disabled={isSubmitting || hasCheckedInToday}
                onClick={hasCheckedInToday ? undefined : handleCheckIn}
              >
                {!isSubmitting && (
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer" />
                )}
                <span className="relative z-10 flex items-center gap-2">
                  {isSubmitting && (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                    />
                  )}
                  {isSubmitting
                    ? 'Checking in...'
                    : hasCheckedInToday
                    ? locale === 'ko'
                      ? '체크인 완료'
                      : 'Checked In'
                    : checkInBtn}
                </span>
              </motion.button>
            ) : (
              <motion.button
                whileHover={!isSubmitting && !hasCheckedInToday ? { scale: 1.01 } : {}}
                whileTap={!isSubmitting && !hasCheckedInToday ? { scale: 0.98 } : {}}
                animate={{
                  opacity: isSubmitting || hasCheckedInToday ? 0.6 : 1,
                  scale: isSubmitting ? 0.98 : 1,
                }}
                className={cls(
                  'py-4 rounded-xl text-white font-bold text-lg shadow-lg flex items-center justify-center gap-2 transition-all mt-10 relative overflow-hidden group mx-auto w-[95%]',
                  'animate-pulse cursor-pointer',
                )}
                style={{ backgroundColor: '#249C68' }}
                onClick={handleLogin}
              >
                {!isSubmitting && (
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer" />
                )}
                <span className="relative z-10 flex items-center gap-2">
                  {isSubmitting && (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                    />
                  )}
                  {loginToPlay}
                </span>
              </motion.button>
            )}
          </div>
        </motion.div>
      </div>

      {/* Bonus Award Modal */}
      <Dialog
        as="div"
        open={bonusModal}
        className="fixed inset-0 w-full h-full z-[9990]"
        onClose={() => setBonusModal(false)}
      >
        <div className="fixed inset-0 z-[9997] bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 z-[9998] flex w-screen items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-lg bg-white">
            {locale === 'ko' ? (
              <>
                <div className="py-4 px-4">
                  <h3 className="text-center text-[#249c68] font-bold text-xl md:text-2xl">세븐 챌린지 완주!</h3>
                </div>
                <div className="my-4 text-center">
                  <p className="text-6xl mb-2">🎉</p>
                  <span className="text-sm lg:text-base font-medium">
                    추가 적립 포인트 <span className="text-[#EF2525] font-bold underline">1,000P</span> 지급 완료
                  </span>
                </div>
                <div className="flex flex-col justify-center px-4 pb-4">
                  <button
                    type="button"
                    onClick={() => setBonusModal(false)}
                    className="text-white bg-[#249c68] py-3 px-8 rounded-lg font-semibold w-full"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="py-4 px-4">
                  <h3 className="text-center text-[#249c68] font-bold text-xl md:text-2xl">
                    Seven Challenge Completed!
                  </h3>
                </div>
                <div className="my-4 text-center">
                  <p className="text-6xl mb-2">🎉</p>
                  <span className="text-sm lg:text-base font-medium">
                    Additional <span className="text-[#EF2525] font-bold underline">1,000P</span> collected
                  </span>
                </div>
                <div className="flex flex-col justify-center px-4 pb-4">
                  <button
                    type="button"
                    onClick={() => setBonusModal(false)}
                    className="text-white bg-[#249c68] py-3 px-8 rounded-lg font-semibold w-full"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}